import { takeLatest } from 'redux-saga'
import { call, put, take, select, all } from 'redux-saga/effects'
import actionTypes from '../actions/actionTypes'
import { adminOnboardingSelectors } from 'redux/selectors'
import { adminOnboardingSync } from '../../sync'
import config from 'config'
import { localize } from 'helpers/translator'
import callAny from 'redux/sagas/helpers/call_any'
import adminOnboardingActions from 'redux/actions/adminOnboardingActions'
import generalActions from 'redux/actions/generalActions'
import userSync from 'sync/user_sync'
import actionUserTypes from 'helpers/enums/actionUserTypes'
import overlayTypes from 'helpers/enums/overlayTypes'
import userSchoolUidSelector from 'redux/selectors/user/user_school_uid_selector'
import formatError from './helpers/error_formatter'

function *schoolConfirmRedirect(action) {
	yield take(actionTypes.ADMIN_ONBOARDING_ANIMATION_COMPLETED)
	yield put({
		type: actionTypes.ADMIN_ONBOARDING_SCHOOL_CONFIRM_DO_REDIRECT_TO_MY_LIBRARY,
		redirectUrl: action.redirectUrl,
	})
}

function *acceptWelcomeInvite() {
	yield take(actionTypes.ADMIN_ONBOARDING_ANIMATION_COMPLETED)
	yield put({
		type: actionTypes.ADMIN_ONBOARDING_ACCEPT_WELCOME_INVITE_REDIRECT,
	})
}

function *searchByInstitution() {
	try {
		const response = yield call(adminOnboardingSync.getUserByInstitution)
		if (response.error_code === 0) {
			let userData = yield select(adminOnboardingSelectors.getStartedAdminOnboarding)
			if (!userData.id) {
				yield take(actionTypes.USER_DATA_FETCHED)
				userData = yield select(adminOnboardingSelectors.getStartedAdminOnboarding)
			}
			const users = response.payload.users
			const userIsMeIndex = users.findIndex(function(element) {
				return element.id === userData.id
			})
			//put user first
			const myself = users.splice(userIsMeIndex, 1)
			users.unshift(myself[0])
			response.payload.users = users
			yield put({
				type: actionTypes.ADMIN_ONBOARDING_GET_USERS_FETCHED,
				payload: response.payload,
			})
		} else {
			yield put({
				type: actionTypes.SYNC_UNCAUGHT_ERROR,
				payload: response.payload,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *addUser(action) {
	try {
		const inviteData = yield select(adminOnboardingSelectors.inviteAdminOnboarding)
		const users = inviteData.users
		const userDuplicated = users.find(function(element) {
			return element.email === action.email
		})
		if (userDuplicated) {
			action.reject({
				email: localize('admin_onboarding_invite_teachers_form_duplicated'),
			})
			yield put({
				type: actionTypes.ADMIN_ONBOARDING_ADD_USER_FAILED,
			})
		} else {
			const response = yield call(adminOnboardingSync.addUser, action.firstName, action.lastName, action.email)
			if (response.error_code === 0) {
				action.resolve()
				yield put({
					type: actionTypes.ADMIN_ONBOARDING_ADD_USER_SUCCESS,
					payload: response.payload,
				})
				yield put({
					type: actionTypes.OPEN_TOP_INFORMATION,
					messageObject: {
						message: response.message,
						timeout: 3000,
						styleType: 'success',
					},
				})
			} else if (response.error_code === 1) {
				const errors = []
				for (const key of Object.keys(response.payload.errors)) {
					errors[key] = response.payload.errors[key]['errorText']
				}
				action.reject({
					...errors,
				})
				yield put({
					type: actionTypes.ADMIN_ONBOARDING_ADD_USER_FAILED,
				})
			}
		}
	} catch (error) {
		// console.log(error)
	}
}

function *inviteUsers() {
	try {
		const selectedUsersSelectorData = yield select(adminOnboardingSelectors.inviteAdminOnboarding)
		const [inviteUsers] = yield all([
			call(adminOnboardingSync.inviteUsers, selectedUsersSelectorData.usersSelectedIds),
			take(actionTypes.ADMIN_ONBOARDING_ANIMATION_COMPLETED),
		])
		if (inviteUsers.error_code === 0) {
			yield put({
				type: actionTypes.ADMIN_ONBOARDING_INVITE_USERS_SUCCESS,
				users: `Users created: ${selectedUsersSelectorData.usersCreated} created - ${selectedUsersSelectorData.usersSuggested} suggested`,
				newUsers: selectedUsersSelectorData.usersCreated,
				existingUsers: selectedUsersSelectorData.usersSuggested,
				schoolName: selectedUsersSelectorData.schoolName,
				step: 3,
			})
		} else {
			yield put({
				type: actionTypes.SYNC_UNCAUGHT_ERROR,
				payload: inviteUsers.payload,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function wait(time) {
	return new Promise(function(resolve) {
		setTimeout(function() {
			resolve(true)
		}, time)
	})
}

function *validateStep(action) {
	try {
		yield callAny(adminOnboardingSync.validateStep, action.path)
	} catch (error) {
		if (error.serverError.payload && error.serverError.payload.redirectUrl) {
			yield put(adminOnboardingActions.stepWhitelistError(error.serverError.payload.redirectUrl))
		} else {
			yield put(adminOnboardingActions.validateStepFailed(config.www + 'library/'))
		}
	}
}

function *confirmSchool(action) {
	let isSchoolConfirmed = false
	if (action.type === actionTypes.ADMIN_ONBOARDING_SCHOOL_CONFIRM_YES) {
		isSchoolConfirmed = true
	}
	try {
		const { duration } = yield all({
			duration: callAny(adminOnboardingSync.confirmSchool, action.institutionId, isSchoolConfirmed),
			animationCompleted: take(actionTypes.ADMIN_ONBOARDING_ANIMATION_COMPLETED),
		})
		if (isSchoolConfirmed) {
			yield put({
				type: actionTypes.ADMIN_ONBOARDING_SCHOOL_CONFIRMED_SUCCESS,
				step: 2,
				durationDays: duration.payload.durationDays,
				isTrialFromSF: false,
			})
		} else {
			yield put({
				type: actionTypes.ADMIN_ONBOARDING_SCHOOL_NOT_CONFIRMED_SUCCESS,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *navigateToStep(action) {
	const adminOnboardingIsAdmin = yield select(adminOnboardingSelectors.getAdminOnboardingIsAdmin)
	const userRolePath = adminOnboardingIsAdmin ? 'admin/' : 'teacher/'
	yield put({
		type: actionTypes.ADMIN_ONBOARDING_TRANSITION_STEP,
		redirectUrl: userRolePath + action.step,
	})
	yield call(wait, 10)
	yield put({
		type: actionTypes.ADMIN_ONBOARDING_RESET_STEP_TRANSITION,
	})
}

function *sendEmailToDM(action) {
	try {
		yield all([
			callAny(adminOnboardingSync.sendEmailToDM, action.firstName, action.lastName, action.email, action.phoneNumber, action.role),
			take(actionTypes.ADMIN_ONBOARDING_ANIMATION_COMPLETED),
		])
		yield put({
			type: actionTypes.ADMIN_ONBOARDING_REFER_A_CONTACT_SENT_SUCCESS,
		})
	} catch (error) {
		// console.log(error)
	}
}

function *joinColleagues() {
	try {
		yield all([
			callAny(adminOnboardingSync.skipStep, 3),
			take(actionTypes.ADMIN_ONBOARDING_ANIMATION_COMPLETED),
		])
		yield put(adminOnboardingActions.joinColleaguesSuccess())
	} catch (error) {
		yield put(adminOnboardingActions.skipStepFailed())
	}
}

function *skipToLastStep() {
	try {
		yield all([
			callAny(adminOnboardingSync.skipStep, 3),
			take(actionTypes.ADMIN_ONBOARDING_ANIMATION_COMPLETED),
		])
		yield put(adminOnboardingActions.skipToLastStepRedirect())
	} catch (error) {
		yield put(adminOnboardingActions.skipStepFailed())
	}
}

function *congratsToNearpodLibrary(action) {
	yield take(actionTypes.ADMIN_ONBOARDING_ANIMATION_COMPLETED)
	yield put({
		type: actionTypes.ADMIN_ONBOARDING_REDIRECT_AFTER_CONGRATS,
	})
	yield take(actionTypes.ADMIN_ONBOARDING_ANIMATION_COMPLETED)
	yield put({
		type: actionTypes.ADMIN_ONBOARDING_CONGRATS_TO_NEARPOD_LIBRARY_REDIRECT,
		redirectUrl: action.redirectUrl,
	})
}

function *noStoreDownloadsGoToMyLibrary() {
	yield callAny(adminOnboardingSync.noStoreDownloadsCompleted)
}

function *noStoreDownloadsExplorerLessons() {
	try {
		yield all([
			callAny(adminOnboardingSync.noStoreDownloadsCompleted),
			take(actionTypes.ADMIN_ONBOARDING_NO_STORE_DOWNLOADS_EXPLORE_LESSONS_METRICS_SUCCESS),
		])
		yield put(adminOnboardingActions.noStoreDownloadsExplorerLessonsSuccess())
	} catch (error) {
		yield put(generalActions.syncUncaughtError())
	}
}

function *shareTeachersAction() {
	try {
		const selectedUsersSelectorData = yield select(adminOnboardingSelectors.inviteAdminOnboarding)
		yield callAny(adminOnboardingSync.inviteUsers, selectedUsersSelectorData.usersSelectedIds)
		yield put(adminOnboardingActions.shareTeachersSuccess())
	} catch (error) {
		yield put(generalActions.syncUncaughtError(error))
	}
}

function *noDMInformationCompleted() {
	yield callAny(adminOnboardingSync.noDMInformationCompleted)
}

function *noDMInformationReferContactClose() {
	try {
		yield callAny(adminOnboardingSync.noDMInformationCompleted)
		yield put(adminOnboardingActions.noDMInformationReferContactCloseSuccess())
	} catch (error) {
		yield put(generalActions.syncUncaughtError(error))
	}
}

function *shareTeachersCompleted() {
	try {
		yield all([
			callAny(adminOnboardingSync.addTeachersCompleted),
			take(actionTypes.ADMIN_ONBOARDING_ANIMATION_COMPLETED),
			take(actionTypes.ADMIN_ONBOARDING_ADD_TEACHERS_SHARE_METRICS_SUCCESS),
		])
		yield put(adminOnboardingActions.shareRedirect())
	} catch (error) {
		yield put(generalActions.syncUncaughtError(error))
	}
}

function *noDMInformationReferContactSend(action) {
	try {
		yield all([
			callAny(adminOnboardingSync.noDMInformationCompleted),
			callAny(adminOnboardingSync.sendEmailToDM, action.firstName, action.lastName, action.email, action.phoneNumber, action.role),
			take(actionTypes.ADMIN_ONBOARDING_ANIMATION_COMPLETED),
			take(actionTypes.ADMIN_ONBOARDING_NO_DM_INFORMATION_REFER_CONTACT_METRICS_SUCCESS),
		])
		yield put(adminOnboardingActions.noDMInformationReferContactSendSuccess())
	} catch (error) {
		yield put(generalActions.syncUncaughtError(error))
	}
}

function *premiumActivitiesGoToMyLibrary() {
	try {
		yield callAny(userSync.loginNotification,
			overlayTypes.FEATURE_ADMIN_ONBOARDING_PREMIUM_ACTIVITIES_OVERLAY_ID,
			null,
			actionUserTypes.ACTION_CANCEL
		)
	} catch (error) {
		yield put(generalActions.syncUncaughtError(error))
	}
}

function *premiumActivitiesAddLesson() {
	try {
		yield callAny(userSync.loginNotification,
			overlayTypes.FEATURE_ADMIN_ONBOARDING_PREMIUM_ACTIVITIES_OVERLAY_ID,
			null,
			actionUserTypes.ACTION_CONTINUE
		)
		yield put(adminOnboardingActions.premiumActivitiesAddLessonCompleted())
	} catch (error) {
		yield put(generalActions.syncUncaughtError(error))
	}
}

function *getTrialData() {
	try {
		const schoolUid = yield select(userSchoolUidSelector)
		if (schoolUid) {
			const response = yield callAny(adminOnboardingSync.getTrialData, schoolUid)
			yield put(adminOnboardingActions.getTrialDataCompleted(response.payload.durationDays, response.payload.isTrialFromSF))
		}
	} catch (error) {
		const serverError = formatError(error)
		yield put(generalActions.syncUncaughtError(serverError))
	}
}

function *getPreSchoolFreeTrialData() {
	try {
		const response = yield callAny(adminOnboardingSync.getPreData)
		yield put(adminOnboardingActions.getTrialDataCompleted(response.payload.durationDays, response.payload.isTrialFromSF))
	} catch (error) {
		const serverError = formatError(error)
		yield put(generalActions.syncUncaughtError(serverError))
	}
}

export default [
	takeLatest(actionTypes.ADMIN_ONBOARDING_SCHOOL_CONFIRM_REDIRECT_TO_MY_LIBRARY, schoolConfirmRedirect),
	takeLatest(actionTypes.ADMIN_ONBOARDING_ACCEPT_WELCOME_INVITE, acceptWelcomeInvite),
	takeLatest([
		actionTypes.ADMIN_ONBOARDING_INITIALIZE_INVITE_VIEW,
		actionTypes.ADMIN_ONBOARDING_ADD_TEACHERS_SHARE_INITIALIZE,
		actionTypes.ADMIN_ONBOARDING_ADD_TEACHERS_INITIALIZE_VIEW,
	], searchByInstitution),
	takeLatest(actionTypes.ADMIN_ONBOARDING_ADD_USER, addUser),
	takeLatest([actionTypes.ADMIN_ONBOARDING_INVITE_USERS, actionTypes.ADMIN_ONBOARDING_INVITE_CONFIRMED], inviteUsers),
	takeLatest(actionTypes.ADMIN_ONBOARDING_INITIALIZE, validateStep),
	takeLatest([actionTypes.ADMIN_ONBOARDING_SCHOOL_CONFIRM_YES, actionTypes.ADMIN_ONBOARDING_SCHOOL_CONFIRM_NO], confirmSchool),
	takeLatest([
		actionTypes.ADMIN_ONBOARDING_NAVIGATE_TO_STEP,
		actionTypes.ADMIN_ONBOARDING_SCHOOL_CONFIRMED_SUCCESS,
		actionTypes.ADMIN_ONBOARDING_INVITE_USERS_SUCCESS,
		actionTypes.ADMIN_ONBOARDING_JOIN_COLLEAGUES_SUCCESS,
	], navigateToStep),
	takeLatest(actionTypes.ADMIN_ONBOARDING_REFER_A_CONTACT_SENT, sendEmailToDM),
	takeLatest(actionTypes.ADMIN_ONBOARDING_JOIN_COLLEAGUES, joinColleagues),
	takeLatest(actionTypes.ADMIN_ONBOARDING_SKIP_TO_LAST_STEP, skipToLastStep),
	takeLatest(actionTypes.ADMIN_ONBOARDING_CONGRATS_TO_NEARPOD_LIBRARY, congratsToNearpodLibrary),
	takeLatest(actionTypes.ADMIN_ONBOARDING_NO_STORE_DOWNLOADS_EXPLORE_LESSONS, noStoreDownloadsExplorerLessons),
	takeLatest(actionTypes.ADMIN_ONBOARDING_NO_STORE_DOWNLOADS_GO_TO_MY_LIBRARY, noStoreDownloadsGoToMyLibrary),
	takeLatest([
		actionTypes.ADMIN_ONBOARDING_ADD_TEACHERS_SHARE_ACTION,
		actionTypes.ADMIN_ONBOARDING_ADD_TEACHERS_SHARE_CONFIRMED,
	], shareTeachersAction),
	takeLatest([actionTypes.ADMIN_ONBOARDING_ADD_TEACHERS_SHARE_COLLEAGUES_SUCCESS, actionTypes.ADMIN_ONBOARDING_ADD_TEACHERS_SHARE_CONFIRMED], shareTeachersCompleted),
	takeLatest(actionTypes.ADMIN_ONBOARDING_NO_DM_INFORMATION_GO_TO_MY_LIBRARY, noDMInformationCompleted),
	takeLatest(actionTypes.ADMIN_ONBOARDING_NO_DM_INFORMATION_REFER_CONTACT_CLOSE, noDMInformationReferContactClose),
	takeLatest(actionTypes.ADMIN_ONBOARDING_NO_DM_INFORMATION_REFER_CONTACT_SEND, noDMInformationReferContactSend),
	takeLatest(actionTypes.ADMIN_ONBOARDING_PREMIUM_ACTIVITIES_ADD_LESSON, premiumActivitiesAddLesson),
	takeLatest(actionTypes.ADMIN_ONBOARDING_PREMIUM_ACTIVITIES_GO_TO_MY_LIBRARY, premiumActivitiesGoToMyLibrary),
	takeLatest([
		actionTypes.ADMIN_ONBOARDING_REFER_CONTACT_INITIALIZE,
		actionTypes.ADMIN_ONBOARDING_INITIALIZE_INVITE_VIEW,
	], getTrialData),
	takeLatest([
		actionTypes.MY_LIBRARY_SCHOOL_TRIAL_ONBOARDING_OVERLAY_INITIALIZE,
		actionTypes.MY_LIBRARY_SCHOOL_USER_ONBOARDING_OVERLAY_INITIALIZE,
	], getPreSchoolFreeTrialData),
]

export {
	noStoreDownloadsExplorerLessons,
	noDMInformationCompleted,
	noDMInformationReferContactClose,
	noDMInformationReferContactSend,
	premiumActivitiesAddLesson,
	premiumActivitiesGoToMyLibrary,
	getTrialData,
	getPreSchoolFreeTrialData,
}
